import React, { useState, useRef, useEffect } from "react";
import { Button, IconButton, Skeleton, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import request from "../utilities/requests";
import { toast } from "../utilities/SnackBarUtils";

export const BANNER_HEIGHT = "50vh";
export const DEFAULT_FOCAL_X = 50;
export const DEFAULT_FOCAL_Y = 30;

const ProfilePhotoBanner = ({
  photoUrl,
  loading = false,
  focalX = DEFAULT_FOCAL_X,
  focalY = DEFAULT_FOCAL_Y,
  onSaved,
  onPickPhoto,
  showCameraBadge = true,
  cameraBadgeProps = {},
  height = BANNER_HEIGHT,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const bannerRef = useRef(null);
  const dragStartRef = useRef(null);

  const [repositionMode, setRepositionMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftX, setDraftX] = useState(focalX);
  const [draftY, setDraftY] = useState(focalY);

  useEffect(() => {
    if (!repositionMode) {
      setDraftX(focalX);
      setDraftY(focalY);
    }
  }, [focalX, focalY, repositionMode]);

  const handleEnter = () => {
    setDraftX(focalX);
    setDraftY(focalY);
    setRepositionMode(true);
  };

  const handleCancel = () => {
    setDraftX(focalX);
    setDraftY(focalY);
    setRepositionMode(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await request("profile/update", "post", {
        photoFocalX: draftX,
        photoFocalY: draftY,
      });
      if (!result.success) throw new Error(result.error);
      setRepositionMode(false);
      toast.success(t("profile.photoposition.saved", "Photo position saved"));
      if (onSaved) onSaved(draftX, draftY);
    } catch (e) {
      toast.error(t("profile.photoposition.error", "Error saving photo position"));
    } finally {
      setSaving(false);
    }
  };

  const handlePointerDown = (e) => {
    if (e.target.closest("button")) return;
    e.preventDefault();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      focalX: draftX,
      focalY: draftY,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragStartRef.current) return;
    const container = bannerRef.current;
    if (!container) return;
    const { width, height: h } = container.getBoundingClientRect();
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const newX = Math.round(
      Math.min(100, Math.max(0, dragStartRef.current.focalX - (dx / width) * 100))
    );
    const newY = Math.round(
      Math.min(100, Math.max(0, dragStartRef.current.focalY - (dy / h) * 100))
    );
    setDraftX(newX);
    setDraftY(newY);
  };

  const handlePointerUp = () => {
    dragStartRef.current = null;
  };

  const displayX = repositionMode ? draftX : focalX;
  const displayY = repositionMode ? draftY : focalY;

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={bannerRef}
        style={{
          position: "relative",
          width: "100%",
          height,
          overflow: "hidden",
          borderBottomLeftRadius: "10rem",
          borderBottomRightRadius: "10rem",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
          background: "#eee",
          cursor: repositionMode ? "grab" : "default",
          touchAction: repositionMode ? "none" : "auto",
        }}
        onPointerDown={repositionMode ? handlePointerDown : undefined}
        onPointerMove={repositionMode ? handlePointerMove : undefined}
        onPointerUp={repositionMode ? handlePointerUp : undefined}
      >
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={height} />
        ) : photoUrl ? (
          <img
            src={photoUrl}
            alt="Profile"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: `${displayX}% ${displayY}%`,
              display: "block",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        ) : (
          <AccountCircleIcon
            style={{
              color: "grey",
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        )}

        {repositionMode && (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="caption"
              style={{
                backgroundColor: "rgba(0,0,0,0.55)",
                color: "white",
                padding: "4px 10px",
                borderRadius: 4,
              }}
            >
              {t("profile.photoposition.hint", "Drag to reposition")}
            </Typography>
          </div>
        )}

        {!repositionMode && photoUrl && (
          <Button
            size="small"
            variant="contained"
            style={{
              position: "absolute",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
            onClick={handleEnter}
          >
            {t("profile.photoposition.button", "Reposition")}
          </Button>
        )}

        {repositionMode && (
          <div
            style={{
              position: "absolute",
              bottom: 16,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 8,
              pointerEvents: "none",
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={saving}
              style={{ pointerEvents: "all" }}
              onClick={handleSave}
            >
              {t("profile.photoposition.save", "Save")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={saving}
              style={{
                backgroundColor: "rgba(255,255,255,0.85)",
                pointerEvents: "all",
              }}
              onClick={handleCancel}
            >
              {t("profile.photoposition.cancel", "Cancel")}
            </Button>
          </div>
        )}
      </div>

      {showCameraBadge && !repositionMode && (
        <IconButton
          aria-label={
            photoUrl
              ? t("profile.changephoto.button")
              : t("profile.addphoto.button")
          }
          onClick={onPickPhoto}
          style={{
            position: "absolute",
            bottom: 28,
            right: 28,
            zIndex: 2,
            width: 46,
            height: 46,
            backgroundColor: "#fff",
            boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
          }}
          {...cameraBadgeProps}
        >
          <PhotoCameraIcon
            style={{ color: theme.palette.primary.main, width: 24, height: 24 }}
          />
        </IconButton>
      )}
    </div>
  );
};

export default ProfilePhotoBanner;
